import React from 'react';
import t from '@jetshop/intl';
import { styled } from 'linaria/react';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import Breadcrumbs from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import MaxWidth from '../../components/Layout/MaxWidth';
import Favourite from '../../components/ProductList/Favourite';
import breadcrumbsStyles from '../../components/breadcrumbsStyles';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import ProductInfoAccordion from './ProductInformation/ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import HelloRetailProductRecomLight from '../../blocks/HelloRetail/HelloRetailProductRecomLight';
import Styled from './Style';
import ProductUSPList from './ProductUSP/ProductUSPList';
import ProductDiscountBadge from '../../components/ProductDiscountBadge/ProductDiscountBadgeProductPage';
import ProductMeta from './ProductMeta';

const RecomContainer = styled(MaxWidth)`
  padding: 0px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
`;

const Product = ({ result: { data, loading }, product }) => {
  var mainImages = 1;
  if (product.hasVariants) {
    mainImages = product.variants.options[0].values.length;
  }
  let initialVariant = useVariantFromUrl();

  // If no specific variation is linked, find the cheapest in stock
  if (
    !Object.keys(initialVariant).length &&
    product?.variants?.values?.length
  ) {
    var currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);
    const variantsInStock = product.variants.values
      .filter(({ stockStatus }) => stockStatus.buyable)
      .sort((a, b) => {
        // prioritize in stock variants stockStatus.stockDate
        const aStockDate = new Date(a.stockStatus?.stockDate || currentDate);
        const bStockDate = new Date(b.stockStatus?.stockDate || currentDate);
        if (
          aStockDate !== bStockDate &&
          (aStockDate > Date(currentDate) || bStockDate > Date(currentDate))
        ) {
          return aStockDate - bStockDate;
        }
        return a.price?.incVat - b.price?.incVat;
      });
    if (variantsInStock.length) {
      initialVariant = { values: variantsInStock[0].values };
    }
  }
  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: true
  });

  const {
    selectedVariant: selectedVariation,
    getMissingOptions
  } = variantHandler;
  const images = product.images ?? [];
  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const productNumbers = [product.articleNumber];
  const video = product?.customFields?.find(({ key }) => key === 'youtube');

  const recomNameAlternatives = { value: 'product_page_alternatives' };
  const titleAlternatives = { value: t('Product Matches') };
  const recomNamePurchased = { value: 'products_page_purchased_together' };
  const titlePurchased = { value: t('Others have also bought') };

  return (
    <Styled.ProductContainer>
      <ProductMeta product={product} />
      <MaxWidth>
        <Breadcrumbs
          parents={parents}
          breadcrumbText={product.breadcrumbText}
          hideLast={true}
          className={breadcrumbsStyles}
        />
      </MaxWidth>
      <Styled.Container>
        <Styled.ProductSection>
          {images.length > 0 && (
            <>
              <Styled.BadgeWrapper>
                <ProductDiscountBadge
                  product={product}
                  variant={selectedVariation}
                />
              </Styled.BadgeWrapper>
              <ImageContainer
                images={images}
                variant={selectedVariation}
                video={video?.listValues ? video.listValues : false}
                mainimages={mainImages}
              />
            </>
          )}
        </Styled.ProductSection>
        <Styled.ProductSection>
          <Styled.HeaderWrapper>
            <div>
              <Styled.ProductTitle
                noMargin
                level="1"
                data-testid="product-title"
              >
                {product.name || 'Loading...'}
              </Styled.ProductTitle>
            </div>
            <Styled.FavouriteButtonWrapper>
              <Favourite
                product={product}
                variant={selectedVariation}
                size="lg"
              />
            </Styled.FavouriteButtonWrapper>
          </Styled.HeaderWrapper>

          <AddToCartForm
            product={product}
            variant={selectedVariation}
            getMissingOptions={getMissingOptions}
            variantHandler={variantHandler}
          />
          <ProductUSPList product={product} variant={selectedVariation} />
        </Styled.ProductSection>
        <Styled.ProductDescriptionContainer>
          <ProductInfoAccordion
            product={product}
            selectedVariation={selectedVariation}
          />
        </Styled.ProductDescriptionContainer>
      </Styled.Container>
      <RecomContainer>
        <HelloRetailProductRecomLight
          recomName={recomNamePurchased}
          title={titlePurchased}
          productNumbers={productNumbers}
        />
        <HelloRetailProductRecomLight
          recomName={recomNameAlternatives}
          title={titleAlternatives}
          productNumbers={productNumbers}
        />
      </RecomContainer>
    </Styled.ProductContainer>
  );
};

const ProductWrapper = props => {
  const { product } = props;

  if (!product)
    return (
      <Styled.ProductContainer>
        <ProductPageLoadingState />
      </Styled.ProductContainer>
    );

  return <Product {...props} />;
};

export default ProductWrapper;
